import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import useSaveAdditionalNotes from '@/hooks/useSaveAdditionalNotes'
import useApproveMedicationRequest from '@/lib/api/med-requests/approve'
import { canViewConversations, isCSViewer, isRoleAllowedToMarkFileViewed } from '@/lib/permissions'
import { selectIsDecisionSubmitting, useMedicationRequestStore } from '@/lib/stores/medication-request'
import { cn } from '@/lib/utils'
import { usePatientFilesGate } from '@/services/patientFiles'
import { useLockedFamilyIds } from '@/services/useLockItem'
import { useUser } from '@clerk/clerk-react'
import { useFlag } from '@unleash/proxy-client-react'
import { type ReactNode, useEffect, useState } from 'react'
import { scrollToSection } from './nav-menu'
import { TooltipWrapper } from './previous-orders'

const ConversationBadge = ({ content }: { content: string }) => {
  return (
    <TooltipWrapper
      trigger={<span className='w-3 h-3 ml-2 rounded-full bg-red-500 shrink-0' />}
      content={<p className='text-white text-xs font-medium leading-4'>{content}</p>}
    />
  )
}

type SecondaryActionProps = {
  label: string
  testId: string
  isDisabled: boolean
  hasConversationBadge: boolean
  onClick: () => void
}

const SecondaryAction = ({ label, testId, isDisabled, hasConversationBadge, onClick }: SecondaryActionProps) => (
  <button
    disabled={isDisabled}
    type='button'
    data-testid={testId}
    onClick={onClick}
    className={cn(
      'flex w-[200px] h-14 justify-center items-center rounded-2xl bg-white py-4 px-6 border border-custom-neutral-200 hover:bg-custom-neutral-200 transition-colors duration-200',
      { 'opacity-50 cursor-not-allowed': isDisabled }
    )}
  >
    <span className='text-[#080F30] text-center text-sm font-semibold leading-[170%]'>{label}</span>
    {hasConversationBadge && <ConversationBadge content='There is a conversation with the patient' />}
  </button>
)

/** State shared by every action footer: scroll and files gates, badges, and common disabled checks. */
export function useFooterActionsState() {
  // True while any decision chain runs, so no other can start on the same treatments.
  const isDecisionSubmitting = useMedicationRequestStore(selectIsDecisionSubmitting)
  const medRequest = useCurrentMedication()
  const data = medRequest?.data

  const isFanFlagEnabled = useFlag('conversation-fan') || data?.latestFanConversationId

  const isRejectionFlagEnabled = useFlag('rejection-fan') || data?.rejectionConversationId

  const [isScrolledDown, setIsScrolledDown] = useState(false)

  // Tracked even for OTC: a multi product order still needs the scroll.
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY
      const documentHeight = document.documentElement.scrollHeight
      const isAtBottom = scrollPosition >= documentHeight * 0.92 // 80% of viewport
      if (isAtBottom) setIsScrolledDown(true)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Check initial scroll position

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Get conversation data to check for messages
  // const { data: followUpConversation } = useGetConversationById({
  //   conversationId: data?.latestFanConversationId ?? '',
  //   enabled: !!data?.latestFanConversationId
  // })

  // const { data: rejectionConversation } = useGetConversationById({
  //   conversationId: data?.rejectionConversationId ?? '',
  //   enabled: !!data?.rejectionConversationId
  // })

  // Every patient file must be viewed first; fail-closed while loading or on error.
  const filesGate = usePatientFilesGate(data?.patient?.id)
  const canMarkFileViewed = isRoleAllowedToMarkFileViewed()
  const isCSViewerRole = isCSViewer()
  // Another prescriber has an item of this order open; family actions would stop partway on it.
  const isFamilyLocked = useLockedFamilyIds(data?.id ?? '').length > 0

  const isFollowupBadgeVisible = isFanFlagEnabled && !!data?.latestFanConversationId

  const isRejectionBadgeVisible = isRejectionFlagEnabled && !!data?.rejectionConversationId

  return {
    data,
    isScrolledDown,
    filesGate,
    isCSViewerRole,
    isFollowupBadgeVisible,
    isRejectionBadgeVisible,
    // Blocks Approve and Submit decision.
    isDecisionBlocked:
      !!data?.locked || isFamilyLocked || isDecisionSubmitting || isCSViewerRole || filesGate.isBlocked,
    // Viewer roles read conversations here, so the files gate only blocks roles that can clear it.
    isFollowUpOrRejectBlocked:
      !canViewConversations() || (filesGate.isBlocked && canMarkFileViewed) || isFamilyLocked || isDecisionSubmitting
  }
}

type FooterActionsProps = {
  state: ReturnType<typeof useFooterActionsState>
  /** Rendered first; takes the primary style from Approve. */
  leadingAction?: ReactNode
  approveLabel: string
  isApprovalDisabled: boolean
  /** Why Approve is disabled; the patient files notice takes the line when both apply. */
  approvalWaitReason?: string | null
  /** Approve without the approval reason sidebar. */
  skipsApprovalReason: boolean
  rejectLabel: string
  isFollowUpDisabled: boolean
  isRejectDisabled: boolean
  /** Called before Approve, Follow Up or Reject opens its flow. */
  onAction?: () => void
}

export function FooterActions({
  state,
  leadingAction,
  approveLabel,
  isApprovalDisabled,
  approvalWaitReason,
  skipsApprovalReason,
  rejectLabel,
  isFollowUpDisabled,
  isRejectDisabled,
  onAction
}: FooterActionsProps) {
  const { data, filesGate, isCSViewerRole, isFollowupBadgeVisible, isRejectionBadgeVisible } = state
  const setSidebarView = useMedicationRequestStore(store => store.setSidebarView)
  const { user } = useUser()

  const { saveAdditionalNotes } = useSaveAdditionalNotes()

  const approveMedicationRequest = useApproveMedicationRequest()
  // Keeps a second click on the OTC path from starting a concurrent approve.
  const [isApproving, setIsApproving] = useState(false)

  const goNextMedRequest = useGoNextMedRequest()

  if (!data) return null

  const roles = user?.publicMetadata.roles ?? []
  const isPharmacist = roles.includes('PHARMACIST')
  const showSkipButton = isPharmacist || data.locked
  const isFilesNoticeShown = filesGate.isBlocked && !data.locked

  return (
    <div className='fixed left-0 bottom-0 flex w-full h-[104px] flex-col justify-center items-center gap-3 shrink-0 border-t border-[#FAFAFB] bg-white shadow-[0px_-8px_80px_0px_rgba(0,0,0,0.10)] z-50'>
      {approvalWaitReason && !isFilesNoticeShown && (
        <p data-testid='approval-wait-reason' className='text-xs font-medium text-custom-neutral-800'>
          {approvalWaitReason}
        </p>
      )}
      {isFilesNoticeShown && (
        <button
          type='button'
          onClick={() => scrollToSection('patient-files')}
          data-testid='patient-files-gate-notice'
          className='text-xs font-medium text-custom-neutral-800 underline underline-offset-2 transition-colors cursor-pointer hover:text-[#153CB7]'
        >
          {filesGate.isError
            ? 'Patient files could not be loaded — Approve, Follow Up and Reject are unavailable'
            : filesGate.isPending
              ? 'Checking patient files…'
              : `${filesGate.unviewedCount} patient ${
                  filesGate.unviewedCount === 1 ? 'file needs' : 'files need'
                } to be viewed before actioning`}
        </button>
      )}
      <div className='flex justify-center gap-4 w-full max-w-[994px]'>
        {leadingAction}
        <button
          type='button'
          data-testid='approve'
          onClick={async () => {
            onAction?.()
            if (skipsApprovalReason) {
              if (isApproving) return
              setIsApproving(true)
              try {
                await approveMedicationRequest()
              } finally {
                setIsApproving(false)
              }
            } else {
              setSidebarView('approval-reason')
            }
          }}
          // Secondary style when a leading action is primary.
          className={cn(
            'flex w-[200px] h-14 flex-col justify-center items-center gap-2 rounded-2xl py-4 px-6 transition-colors duration-200',
            leadingAction
              ? 'bg-white border border-custom-neutral-200 hover:bg-custom-neutral-200'
              : 'bg-[#153CB7] hover:bg-[#0F2E8A]',
            { 'opacity-50 cursor-not-allowed': isApprovalDisabled || isApproving }
          )}
          disabled={isApprovalDisabled || isApproving}
        >
          <span
            className={cn(
              'text-center text-sm font-semibold leading-[170%]',
              leadingAction ? 'text-[#080F30]' : 'text-white'
            )}
          >
            {approveLabel}
          </span>
        </button>
        <SecondaryAction
          label='Follow Up'
          testId='follow-up'
          isDisabled={isFollowUpDisabled}
          hasConversationBadge={!!isFollowupBadgeVisible}
          onClick={() => {
            onAction?.()
            setSidebarView('contact-patient')
          }}
        />
        <SecondaryAction
          label={rejectLabel}
          testId='reject'
          isDisabled={isRejectDisabled}
          hasConversationBadge={!!isRejectionBadgeVisible}
          onClick={() => {
            onAction?.()
            setSidebarView('rejection')
          }}
        />

        {showSkipButton && (
          <button
            type='button'
            data-testid='skip-to-next'
            onClick={() => {
              if (isCSViewerRole) return
              if (isPharmacist && !data.locked) saveAdditionalNotes()
              goNextMedRequest()
            }}
            className={cn(
              'flex w-[200px] h-14 flex-col justify-center items-center gap-2 rounded-2xl bg-[#153CB7] py-4 px-6 hover:bg-[#0F2E8A] transition-colors duration-200',
              {
                'opacity-50 cursor-not-allowed': isCSViewerRole
              }
            )}
            disabled={isCSViewerRole}
          >
            <span className='text-white text-center text-sm font-semibold leading-[170%]'>Skip to Next</span>
          </button>
        )}
      </div>
    </div>
  )
}
